import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { FiTrendingUp, FiClock, FiCheckCircle, FiRefreshCw } from "react-icons/fi";
import axios from "axios";
import { useSnackbar } from "notistack";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  position: relative;
  will-change: transform, opacity;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  border-left: 4px solid ${props => props.$color};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeIn} 0.3s ease forwards;
  position: relative;
  overflow: hidden;
  z-index: 1;
  will-change: transform;
  opacity: 0; /* Inicialmente invisível para a animação */

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: 0.5s;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    
    &:before {
      left: 100%;
    }
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 122, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || '#007aff'};
  font-size: 1.5rem;
`;

const StatContent = styled.div`
  flex: 1;

  h3 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const StatTrend = styled.div`
  font-size: 0.85rem;
  color: ${props => props.$positive ? '#34A853' : '#EA4335'};
`;

const SkeletonLoader = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '80%'};
  margin-bottom: ${props => props.marginBottom || '10px'};

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// Cache simples em memória
const statsCache = {
  data: null,
  timestamp: 0,
  activeFilter: null
};

const CACHE_EXPIRATION = 300000; // 5 minutos em milissegundos

const StatsGrid = ({ userPlan, activeFilter }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState(() => ({
    totalRelocations: 0,
    inProgress: 0,
    completed: 0,
    completionRate: 0,
    comparison: {
      percentage: 0,
      trend: 'up'
    }
  }));
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const mapPeriodToApiParam = useCallback((period) => {
    const periodMap = {
      'diario': 'daily',
      'semanal': 'weekly',
      'mensal': 'monthly',
      'anual': 'yearly'
    };
    return periodMap[period] || 'monthly';
  }, []);

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    return `${baseUrl.replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}`;
  };

  const fetchStats = useCallback(async (period) => {
    // Verificar cache primeiro
    const now = Date.now();
    if (statsCache.data && 
        statsCache.activeFilter === period && 
        now - statsCache.timestamp < CACHE_EXPIRATION) {
      setStats(statsCache.data);
      setIsInitialLoad(false);
      return;
    }

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user.token;

      if (!token) {
        throw new Error("Usuário não autenticado");
      }

      const apiPeriod = mapPeriodToApiParam(period);
      const response = await axios.get(buildApiUrl("dashboard/stats"), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        params: { period: apiPeriod }
      });

      // Atualizar cache
      statsCache.data = response.data;
      statsCache.timestamp = now;
      statsCache.activeFilter = period;

      setStats(response.data);
      setIsInitialLoad(false);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao carregar estatísticas", 
        { variant: "error" }
      );
      
      // Tentar usar dados do cache mesmo que desatualizados se a requisição falhar
      if (statsCache.data) {
        setStats(statsCache.data);
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, mapPeriodToApiParam]);

  useEffect(() => {
    if (userPlan !== 'Free') {
      // Prefetch dos dados quando o componente é montado
      fetchStats(activeFilter);
    } else {
      setIsInitialLoad(false);
    }
  }, [activeFilter, userPlan, fetchStats]);

  const getTrendText = useCallback((trend, percentage) => {
    if (percentage === undefined || percentage === null) return 'Dados indisponíveis';
    const trendText = trend === 'up' ? '+' : '-';
    return `${trendText}${Math.abs(percentage)}% em relação ao período anterior`;
  }, []);

  // Renderização otimizada dos cards
  const renderCard = (icon, title, value, color, showTrend = true) => (
    <StatCard $color={color}>
      <StatIcon $color={color}>
        {icon}
      </StatIcon>
      <StatContent>
        <h3>{title}</h3>
        {loading && isInitialLoad ? (
          <>
            <SkeletonLoader height="28px" width="60px" marginBottom="8px" />
            <SkeletonLoader height="16px" width="100%" />
          </>
        ) : (
          <>
            <StatValue>{value}</StatValue>
            {showTrend && (
              <StatTrend $positive={stats.comparison?.trend === 'up'}>
                {getTrendText(stats.comparison?.trend, stats.comparison?.percentage)}
              </StatTrend>
            )}
          </>
        )}
      </StatContent>
    </StatCard>
  );

  return (
    <StatsGridContainer>
      {renderCard(
        <FiTrendingUp />,
        "Total Realocações",
        stats.totalRelocations || 0,
        "#4285F4"
      )}
      
      {renderCard(
        <FiClock />,
        "Em Andamento",
        stats.inProgress || 0,
        "#EA4335"
      )}
      
      {renderCard(
        <FiCheckCircle />,
        "Concluídas",
        stats.completed || 0,
        "#34A853"
      )}
      
      {userPlan !== 'Free' && renderCard(
        <FiRefreshCw />,
        "Taxa de Conclusão",
        stats.completionRate ? `${stats.completionRate}%` : '--',
        "#673AB7",
        false
      )}
    </StatsGridContainer>
  );
};

export default React.memo(StatsGrid);